// ============================================
// JOB EDIT PAGE
// ============================================

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Save, Search } from 'lucide-react';
import { PageHeader } from '@/components';
import { useUpdateJob } from '@/hooks';
import { jobsApi } from '@/services/api';
import { departmentLabels, vesselTypeLabels, countries } from '@/utils';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_authenticated/jobs/$id/edit')({
  component: JobEditPage,
});

const jobSchema = z.object({
  title: z.string().min(5, 'Başlık en az 5 karakter olmalıdır'),
  description: z.string().min(50, 'Açıklama en az 50 karakter olmalıdır'),
  department: z.string().optional(),
  rankId: z.string().optional(),
  vesselType: z.string().min(1, 'Gemi tipi seçiniz'),
  vesselName: z.string().optional(),
  salaryMin: z.number().min(0, 'Geçerli bir maaş giriniz'),
  salaryMax: z.number().min(0, 'Geçerli bir maaş giriniz'),
  salaryCurrency: z.string().default('USD'),
  contractDuration: z.number().min(1, 'Kontrat süresi giriniz'),
  experienceMin: z.number().min(0).default(0),
  joinDate: z.string().optional(),
  expiresAt: z.string().optional(),
  hasEcdis: z.boolean().optional(),
  hasSatelliteInternet: z.boolean().optional(),
});

type JobFormData = z.infer<typeof jobSchema>;

function JobEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const updateJobMutation = useUpdateJob();

  // Fetch job details - get from my jobs and filter
  const { data: rawJobsData, isLoading: jobLoading } = useQuery({
    queryKey: ['jobs', 'me', 'all'],
    queryFn: () => jobsApi.getMyJobs({ limit: 50 }),
    enabled: !!id,
  });

  // Find the specific job by ID - handle nested response wrapper
  const jobsResponse = (rawJobsData as any)?.data || rawJobsData;
  const jobsList = (jobsResponse as any)?.data || jobsResponse || [];
  const job = Array.isArray(jobsList) ? jobsList.find((j: any) => j.id === id) : null;

  // İlan detayını slug ile çek (requirements dahil)
  const { data: rawJobDetail } = useQuery({
    queryKey: ['job-detail', job?.slug],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'}/jobs/${job.slug}`);
      return response.json();
    },
    enabled: !!job?.slug,
  });

  const jobDetail = (rawJobDetail as any)?.data || rawJobDetail;

  // Fetch ranks for dropdown
  const { data: rawRanksData } = useQuery({
    queryKey: ['ranks'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'}/common/ranks`);
      return response.json();
    },
  });

  // Handle backend response wrapper for ranks
  const ranksResponse = rawRanksData?.data || rawRanksData;
  const ranksList = Array.isArray(ranksResponse?.data) 
    ? ranksResponse.data 
    : (Array.isArray(ranksResponse) ? ranksResponse : []);

  // Sertifika tiplerini çek
  const { data: rawCertData } = useQuery({
    queryKey: ['certificate-types'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'}/common/certificate-types`);
      return response.json();
    },
    staleTime: 10 * 60 * 1000,
  });

  const certTypesList: any[] = (() => {
    if (!rawCertData) return [];
    if (Array.isArray(rawCertData)) return rawCertData;
    const resp = rawCertData as any;
    if (resp.data) {
      if (Array.isArray(resp.data)) return resp.data;
      if (resp.data.data && Array.isArray(resp.data.data)) return resp.data.data;
    }
    return [];
  })();

  // Seçili vatandaşlık kodları (max 3)
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [natSearch, setNatSearch] = useState('');
  const [natInitialized, setNatInitialized] = useState(false);

  const filteredCountries = countries.filter((c) => {
    if (!natSearch) return true;
    return c.name.toLowerCase().includes(natSearch.toLowerCase()) || c.code.toLowerCase().includes(natSearch.toLowerCase());
  });

  const toggleNationality = (code: string) => {
    setSelectedNationalities(prev => {
      if (prev.includes(code)) return prev.filter(c => c !== code);
      if (prev.length >= 3) return prev;
      return [...prev, code];
    });
  };

  // Seçili sertifika ID'leri
  const [selectedCertificates, setSelectedCertificates] = useState<string[]>([]);
  const [certSearch, setCertSearch] = useState('');
  const [certsInitialized, setCertsInitialized] = useState(false);

  const filteredCertTypes = certTypesList.filter((ct: any) => {
    if (!certSearch) return true;
    const search = certSearch.toLowerCase();
    return (ct.nameTr?.toLowerCase().includes(search) || ct.nameEn?.toLowerCase().includes(search) || ct.code?.toLowerCase().includes(search));
  });

  const toggleCertificate = (certId: string) => {
    setSelectedCertificates(prev =>
      prev.includes(certId) ? prev.filter(c => c !== certId) : [...prev, certId]
    );
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
  });

  // Populate form when job data loads
  useEffect(() => {
    if (job) {
      reset({
        title: job.title || '',
        description: job.description || '',
        department: job.department || '',
        rankId: job.rankId || '',
        vesselType: job.vesselType || '',
        vesselName: job.vesselName || '',
        salaryMin: job.salaryMin || 0,
        salaryMax: job.salaryMax || 0,
        salaryCurrency: job.salaryCurrency || 'USD',
        contractDuration: job.contractDuration || 6,
        experienceMin: job.experienceMin || 0,
        joinDate: job.joinDate ? job.joinDate.split('T')[0] : '',
        expiresAt: job.expiresAt ? job.expiresAt.split('T')[0] : '',
        hasEcdis: !!job.hasEcdis,
        hasSatelliteInternet: !!job.hasSatelliteInternet,
      });

      // Vatandaşlık seçimlerini yükle
      if (!natInitialized && job.preferredNationality) {
        const codes = job.preferredNationality.split(',').filter(Boolean);
        setSelectedNationalities(codes.slice(0, 3));
        setNatInitialized(true);
      }
    }
  }, [job, reset, natInitialized]);

  // Sertifikaları jobDetail'den yükle
  useEffect(() => {
    if (jobDetail?.requirements && Array.isArray(jobDetail.requirements) && !certsInitialized) {
      const certIds = jobDetail.requirements.map((r: any) => r.certificateTypeId).filter(Boolean);
      if (certIds.length > 0) {
        setSelectedCertificates(certIds);
        setCertsInitialized(true);
      }
    }
  }, [jobDetail, certsInitialized]);

  const selectedDepartment = watch('department');

  const onSubmit = async (data: JobFormData) => {
    try {
      const deckRanks = ranksList.filter((r: any) => r.department === 'DECK');
      const defaultRankId = deckRanks[0]?.id || ranksList[0]?.id || '';

      await updateJobMutation.mutateAsync({
        id,
        data: {
          ...data,
          department: 'DECK',
          rankId: defaultRankId,
          requirements: selectedCertificates,
          ...(selectedNationalities.length > 0 ? { preferredNationality: selectedNationalities.join(',') } : {}),
        },
      });
      navigate({ to: `/jobs/${job?.slug || id}` });
    } catch (error) {
      console.error('Job update failed:', error);
    }
  };

  if (jobLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-maritime-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="İlanı Düzenle"
        description={job?.title}
        actions={
          <Link to="/jobs/$slug" params={{ slug: job?.slug || id }} className="btn-secondary">
            <ArrowLeft className="w-4 h-4" />
            Geri
          </Link>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Temel Bilgiler</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="label">İlan Başlığı *</label>
              <input
                type="text"
                className={`input ${errors.title ? 'input-error' : ''}`}
                placeholder="örn: Chief Officer - Tanker"
                {...register('title')}
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
              )}
            </div>

            {/* Vessel Type */}
            <div>
              <label className="label">Gemi Tipi *</label>
              <select
                className={`input ${errors.vesselType ? 'input-error' : ''}`}
                {...register('vesselType')}
              >
                <option value="">Seçiniz</option>
                {Object.entries(vesselTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.vesselType && (
                <p className="mt-1 text-xs text-red-500">{errors.vesselType.message}</p>
              )}
            </div>

            {/* Vessel Name */}
            <div>
              <label className="label">Gemi Adı</label>
              <input
                type="text"
                className="input"
                placeholder="örn: MV Ocean Star"
                {...register('vesselName')}
              />
            </div>

            {/* Has ECDIS */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hasEcdis"
                className="w-4 h-4 text-maritime-600 rounded"
                {...register('hasEcdis')}
              />
              <label htmlFor="hasEcdis" className="text-sm text-gray-700">ECDIS Mevcut</label>
            </div>

            {/* Has Satellite Internet */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hasSatelliteInternet"
                className="w-4 h-4 text-maritime-600 rounded"
                {...register('hasSatelliteInternet')}
              />
              <label htmlFor="hasSatelliteInternet" className="text-sm text-gray-700">Uydu İnterneti Mevcut</label>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="label">Açıklama *</label>
              <textarea
                className={`input min-h-[150px] ${errors.description ? 'input-error' : ''}`}
                placeholder="İş tanımı, sorumluluklar ve beklentiler..."
                {...register('description')}
              />
              {errors.description && (
                <p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Salary & Contract */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Maaş & Kontrat</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Salary Min */}
            <div>
              <label className="label">Minimum Maaş *</label>
              <input
                type="number"
                className={`input ${errors.salaryMin ? 'input-error' : ''}`}
                placeholder="5000"
                {...register('salaryMin', { valueAsNumber: true })}
              />
              {errors.salaryMin && (
                <p className="mt-1 text-xs text-red-500">{errors.salaryMin.message}</p>
              )}
            </div>

            {/* Salary Max */}
            <div>
              <label className="label">Maksimum Maaş *</label>
              <input
                type="number"
                className={`input ${errors.salaryMax ? 'input-error' : ''}`}
                placeholder="7000"
                {...register('salaryMax', { valueAsNumber: true })}
              />
              {errors.salaryMax && (
                <p className="mt-1 text-xs text-red-500">{errors.salaryMax.message}</p>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="label">Para Birimi</label>
              <select className="input" {...register('salaryCurrency')}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="TRY">TRY</option>
              </select>
            </div>

            {/* Contract Duration */}
            <div>
              <label className="label">Kontrat Süresi (Ay) *</label>
              <input
                type="number"
                className={`input ${errors.contractDuration ? 'input-error' : ''}`}
                placeholder="6"
                {...register('contractDuration', { valueAsNumber: true })}
              />
              {errors.contractDuration && (
                <p className="mt-1 text-xs text-red-500">{errors.contractDuration.message}</p>
              )}
            </div>

            {/* Experience Min */}
            <div>
              <label className="label">Minimum Deneyim (Yıl)</label>
              <input
                type="number"
                className="input"
                placeholder="0"
                {...register('experienceMin', { valueAsNumber: true })}
              />
            </div>

            {/* Join Date */}
            <div>
              <label className="label">Başlama Tarihi</label>
              <input
                type="date"
                className="input"
                {...register('joinDate')}
              />
            </div>

            {/* Expiration Date */}
            <div>
              <label className="label">İlan Bitiş Tarihi</label>
              <input
                type="date"
                className="input"
                {...register('expiresAt')}
              />
            </div>
          </div>
        </div>

        {/* Tercih Edilen Vatandaşlık */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Tercih Edilen Vatandaşlık
            <span className="ml-2 text-sm font-normal text-gray-400">(En fazla 3 seçim)</span>
          </h3>

          {/* Seçili vatandaşlıklar - tag */}
          {selectedNationalities.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedNationalities.map(code => {
                const country = countries.find(c => c.code === code);
                return country ? (
                  <span key={code} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                    {country.name}
                    <button type="button" onClick={() => toggleNationality(code)} className="ml-1 text-blue-400 hover:text-blue-600">&times;</button>
                  </span>
                ) : null;
              })}
            </div>
          )}

          {/* Arama */}
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="input pl-10"
              placeholder="Ülke ara..."
              value={natSearch}
              onChange={e => setNatSearch(e.target.value)}
            />
          </div>

          {/* Ülke listesi */}
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {filteredCountries.map((country) => {
              const isSelected = selectedNationalities.includes(country.code);
              const isDisabled = !isSelected && selectedNationalities.length >= 3;
              return (
                <label key={country.code} className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${isDisabled ? 'opacity-40' : 'hover:bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleNationality(country.code)}
                    disabled={isDisabled}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{country.name}</span>
                </label>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500">{selectedNationalities.length}/3 ülke seçildi</p>
        </div>

        {/* Gerekli Sertifikalar */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gerekli Sertifikalar</h3>
          
          <div>
            <label className="label">Sertifika Gereksinimleri (Opsiyonel)</label>
            
            {/* Seçili sertifikalar */}
            {selectedCertificates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedCertificates.map(certId => {
                  const cert = certTypesList.find((c: any) => c.id === certId);
                  return cert ? (
                    <span key={certId} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      {cert.nameTr || cert.nameEn}
                      <button type="button" onClick={() => toggleCertificate(certId)} className="ml-1 text-blue-400 hover:text-blue-600">&times;</button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Arama */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Sertifika ara..."
                value={certSearch}
                onChange={e => setCertSearch(e.target.value)}
              />
            </div>

            {/* Sertifika listesi */}
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {filteredCertTypes.length === 0 ? (
                <p className="p-3 text-sm text-gray-500 text-center">Sertifika bulunamadı</p>
              ) : (
                filteredCertTypes.map((cert: any) => (
                  <label key={cert.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCertificates.includes(cert.id)}
                      onChange={() => toggleCertificate(cert.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{cert.nameTr || cert.nameEn}</p>
                      {cert.code && <p className="text-xs text-gray-400">{cert.code}</p>}
                    </div>
                  </label>
                ))
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {selectedCertificates.length} sertifika seçildi
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/jobs/$slug" params={{ slug: job?.slug || id }} className="btn-secondary">
            İptal
          </Link>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || updateJobMutation.isPending}
          >
            {(isSubmitting || updateJobMutation.isPending) ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Değişiklikleri Kaydet
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
